import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import useAppSettings from '../../../hooks/useAppSettings';
import GradientPainter from '../../common/GradientPainter/GradientPainter';
import Logo from '../../common/Logo/Logo';
import useAnalytics from '../../../hooks/useAnalytics';

import './ClearView.scss';

import bearPatch from '../../../assets/patches/bear_patch.svg';
import cobraPatch from '../../../assets/patches/cobra_patch.svg';
import delta_earthPatch from '../../../assets/patches/delta_earth_patch.svg';
import dragonPatch from '../../../assets/patches/dragon_patch.svg';
import hammerPatch from '../../../assets/patches/hammer_patch.svg';
import hydraPatch from '../../../assets/patches/hydra_patch.svg';
import griffonsPatch from '../../../assets/patches/griffons_patch.png';
import knightPatch from '../../../assets/patches/knight_patch.svg';
import octopusPatch from '../../../assets/patches/octopus_patch.svg';
import owlPatch from '../../../assets/patches/owl_patch.svg';
import sscPatch from '../../../assets/patches/ssc_patch.svg';
import starsPatch from '../../../assets/patches/stars_patch.svg';
import swordPatch from '../../../assets/patches/sword_patch.svg';
import tigerPatch from '../../../assets/patches/tiger_patch.svg';

const ClearView = () => {
  const { homepageData } = useAppSettings();
  const { actions } = useApp();

  const [gameState, setGameState] = useState('intro');
  const [currentLevel, setCurrentLevel] = useState(1);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [cards, setCards] = useState([]);
  const [showingAll, setShowingAll] = useState(false);
  const [previewComplete, setPreviewComplete] = useState(false);
  const [cardsLocked, setCardsLocked] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [backgroundBlurred, setBackgroundBlurred] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [levelTimes, setLevelTimes] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [waitingForImages, setWaitingForImages] = useState(false);
  const [levelLoadProgress, setLevelLoadProgress] = useState(0);
  const { trackViewFinderLoad, trackViewFinderStart, trackViewFinderLevelOneStart, trackViewFinderLevelOneFinish, trackViewfinderNextLevel, trackViewFinderLevelTwoStart, trackViewFinderLevelTwoFinish, trackViewFinderLevelThreeStart, trackViewFinderLevelThreeFinish,trackViewFinderPlayAgain } = useAnalytics();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackViewFinderLoad();
      hasTrackedRef.current = true;
    }
  }, [trackViewFinderLoad]);

  const PATCH_ICONS = {
    1: tigerPatch,
    2: bearPatch,
    3: cobraPatch,
    4: delta_earthPatch,
    5: dragonPatch,
    6: hammerPatch,
    7: hydraPatch,
    8: knightPatch,
    9: octopusPatch,
    10: owlPatch,
    11: sscPatch,
    12: starsPatch,
    13: swordPatch,
    14: griffonsPatch
  };

  const BACKGROUND_IMAGES = {
    1: homepageData?.viewfinder_round_1_image?.url,
    2: homepageData?.viewfinder_round_2_image?.url,
    3: homepageData?.viewfinder_round_3_image?.url
  };

  const LEVEL_CONFIG = {
    1: { rows: 4, cols: 2, pairs: 4 },
    2: { rows: 4, cols: 3, pairs: 6 },
    3: { rows: 7, cols: 4, pairs: 14 }
  };

  const preloadImages = useCallback(() => {
    return new Promise((resolve) => {
      const immediateImages = [
        ...Object.values(PATCH_ICONS),
        BACKGROUND_IMAGES[1]
      ].filter(Boolean);

      const laterBackgrounds = [BACKGROUND_IMAGES[2], BACKGROUND_IMAGES[3]].filter(
        Boolean
      );

      if (!immediateImages.length) {
        setImagesLoaded(true);
        resolve();
        return;
      }

      const loadImage = (src) =>
        new Promise((imgResolve) => {
          const img = new Image();
          const done = () => imgResolve();
          img.onload = done;
          img.onerror = done;
          img.decoding = 'async';
          img.referrerPolicy = 'no-referrer';
          img.src = src;
        });

      Promise.all(immediateImages.map(loadImage)).then(() => {
        setImagesLoaded(true);
        resolve();

        const idlePreload = (urls) => {
          urls.forEach((url) => {
            if (!url) return;
            const img = new Image();
            img.decoding = 'async';
            img.referrerPolicy = 'no-referrer';
            img.src = url;
          });
        };

        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          window.requestIdleCallback(() => idlePreload(laterBackgrounds), {
            timeout: 1500
          });
        } else {
          setTimeout(() => idlePreload(laterBackgrounds), 1200);
        }
      });
    });
  }, [homepageData]);

  const preloadLevelAssets = useCallback(
    (levelCards, level) => {
      const uniqueIcons = Array.from(
        new Set(levelCards.map((card) => card.icon))
      );
      const sources = [...uniqueIcons, BACKGROUND_IMAGES[level]].filter(
        (src) => src
      );

      if (!sources.length) {
        setLevelLoadProgress(100);
        return Promise.resolve();
      }

      let loaded = 0;
      const totalSources = sources.length;

      return Promise.all(
        sources.map(
          (src) =>
            new Promise((resolve) => {
              const img = new Image();
              const markLoaded = () => {
                loaded += 1;
                setLevelLoadProgress(
                  Math.round((loaded / totalSources) * 100)
                );
                resolve();
              };

              img.onload = markLoaded;
              img.onerror = markLoaded;
              img.src = src;
            })
        )
      ).then(() => {
        setLevelLoadProgress(100);
      });
    },
    [homepageData]
  );

  useEffect(() => {
    preloadImages();
  }, [preloadImages]);

  const handleBackToHome = () => {
    actions.setView('home');
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const initializeLevel = useCallback((level) => {
  
    if (level) {
      if (level === 1) {
        trackViewFinderLevelOneStart();
      }
      if (level === 2) {
        trackViewFinderLevelTwoStart();
      }
      if (level === 3) {
        trackViewFinderLevelThreeStart();
      }
    }


    const config = LEVEL_CONFIG[level];
    const pairs = config.pairs;

    const cardPairs = [];
    for (let i = 1; i <= pairs; i++) {
      cardPairs.push(
        { id: `${i}-a`, icon: PATCH_ICONS[i], pairId: i },
        { id: `${i}-b`, icon: PATCH_ICONS[i], pairId: i }
      );
    }

    const shuffledCards = shuffleArray(cardPairs);
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedPairs([]);
    setFeedback('');
    setPreviewComplete(false);
    setBackgroundBlurred(true);
    setCardsLocked(true);
    return shuffledCards;
  }, [trackViewFinderLevelOneStart, trackViewFinderLevelTwoStart, trackViewFinderLevelThreeStart]);

  const startLevelPreview = useCallback(
    async (level, levelCards) => {
      setLevelLoadProgress(0);
      setWaitingForImages(true);
      setCardsLocked(true);

      await preloadLevelAssets(levelCards, level);

      setWaitingForImages(false);
      setStartTime(Date.now());
      setShowingAll(true);

      setTimeout(() => {
        setShowingAll(false);
        setPreviewComplete(true);

        setTimeout(() => {
          setBackgroundBlurred(false);
          setCardsLocked(false);
        }, 800);
      }, 3000);
    },
    [preloadLevelAssets]
  );

  const startGame = () => {
    if (!imagesLoaded) return;
    trackViewFinderStart();
    setGameState('playing');
    setCurrentLevel(1);
    setLevelTimes([]);
    const newCards = initializeLevel(1);
    startLevelPreview(1, newCards);
  };

   const playAgain = () => {
    if (!imagesLoaded) return;
    trackViewFinderPlayAgain();
    setGameState('playing');
    setCurrentLevel(1);
    setLevelTimes([]);
    const newCards = initializeLevel(1);
    startLevelPreview(1, newCards);
  };

  const handleCardClick = (cardId) => {
    if (
      waitingForImages ||
      showingAll ||
      cardsLocked ||
      flippedCards.includes(cardId) ||
      matchedPairs.some((pair) => pair.includes(cardId))
    ) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setCardsLocked(true);

      const [firstCard, secondCard] = newFlippedCards.map((id) =>
        cards.find((card) => card.id === id)
      );

      if (firstCard.pairId === secondCard.pairId) {
        setFeedback('Nice!');

        setTimeout(() => {
          setMatchedPairs((prev) => [...prev, newFlippedCards]);
          setFlippedCards([]);
          setFeedback('');
          setCardsLocked(false);
        }, 800);

        const newMatchedCount = matchedPairs.length + 1;
        if (newMatchedCount === LEVEL_CONFIG[currentLevel].pairs) {
          const completionTime = Math.floor((Date.now() - startTime) / 1000);
          setLevelTimes((prev) => [
            ...prev,
            { level: currentLevel, time: completionTime }
          ]);

          setTimeout(() => {
            if (currentLevel === 3) {
              trackViewFinderLevelThreeFinish();
              setGameState('gameComplete');
            } else {
              setGameState('levelComplete');
              if (currentLevel === 1) {
                trackViewFinderLevelOneFinish();
              }
              if (currentLevel === 2) {
                trackViewFinderLevelTwoFinish();
              }

            }
          }, 3500);
        }
      } else {
        setFeedback('Try again!');
        setTimeout(() => {
          setFlippedCards([]);
          setFeedback('');
          setCardsLocked(false);
        }, 750);
      }
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (remainingSeconds === 0) {
      return `${minutes}m`;
    }

    return `${minutes}m${remainingSeconds}s`;
  };

  const nextLevel = () => {
    trackViewfinderNextLevel(currentLevel);
    const newLevel = currentLevel + 1;
    setCurrentLevel(newLevel);
    setGameState('playing');
    const newCards = initializeLevel(newLevel);
    startLevelPreview(newLevel, newCards);
  };

  const renderCard = (card) => {
    const isFlipped =
      showingAll ||
      flippedCards.includes(card.id) ||
      matchedPairs.some((pair) => pair.includes(card.id));
    const isMatched = matchedPairs.some((pair) => pair.includes(card.id));

    return (
      <div
        key={card.id}
        className={`game-card ${isFlipped ? 'flipped' : ''} ${isMatched ? 'matched' : ''} ${cardsLocked ? 'locked' : ''}`}
        onClick={() => handleCardClick(card.id)}
      >
        <div className="card-inner">
          <div className="card-back"></div>
          <div className="card-front">
            <img
              src={card.icon}
              alt="patch icon"
              className={card.icon.endsWith('.svg') ? 'patch-svg' : 'patch-png'}
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderGameGrid = () => {
    const config = LEVEL_CONFIG[currentLevel];
    const bgImage = BACKGROUND_IMAGES[currentLevel];
    return (
      <div className="game-grid-container">
        <div
          className={`background-reveal ${backgroundBlurred ? 'blur' : ''}`}
          style={{
            backgroundImage: bgImage ? `url(${bgImage})` : 'none'
          }}
        >
          <div className="placeholder-bg">
            <div className="placeholder-content"></div>
          </div>
        </div>

        <div
          className={`game-grid level-${currentLevel}`}
          style={{
            gridTemplateColumns: `repeat(${config.cols}, 1fr)`,
            gridTemplateRows: `repeat(${config.rows}, 1fr)`
          }}
        >
          {cards.map(renderCard)}
        </div>
      </div>
    );
  };

  const getInstructionText = () => {
    if (waitingForImages) {
      return 'Preparing...';
    }
    if (showingAll) {
      return homepageData?.viewfinder_pre_flip_instructions;
    }
    if (previewComplete) {
      return homepageData?.viewfinder_flip_instructions;
    }
    return homepageData?.viewfinder_pre_flip_instructions;
  };

  return (
    <div className="clear-view gradient-paint">
      <GradientPainter />
      <div className="gradient-background"></div>

      {waitingForImages && gameState === 'playing' && (
        <div className="loading-overlay">
          <div className="loading-content">
            <h3>Loading cards...</h3>
            <div className="loading-bar">
              <div
                className="loading-progress"
                style={{ width: `${levelLoadProgress}%` }}
              ></div>
            </div>
            <p>Starting as soon as everything is ready.</p>
          </div>
        </div>
      )}

      {gameState === 'intro' && (
        <div className="intro-screen">
          <div className="slides-topbar-main game-screen">
            <div className="logo-group logo-group--main">
              <div className="logo logo-main-container">
                <Logo
                  className="logo-main"
                  src={
                    homepageData?.level_62_logo_white?.url ||
                    homepageData?.level_62_logo_white
                  }
                  alt={
                    homepageData?.level_62_logo_white?.alt || 'Level 62 Logo'
                  }
                />
              </div>
            </div>
          </div>
          <div className="intro-content">
            <h1 className="game-title">
              {homepageData?.viewfinder_game_title || 'Viewfinder'}
            </h1>
            <p className="game-description">
              {homepageData?.viewfinder_intro_screen_copy_1}
            </p>
            <div className="game-demo-images piped">
              {[1, 2, 3].map((num) => (
                <div key={num} className="game-card">
                  <img
                    src={PATCH_ICONS[num]}
                    alt="patch icon"
                    className={PATCH_ICONS[num].endsWith('.svg') ? 'patch-svg' : 'patch-png'}
                    loading="eager"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
            <p className="game-instructions">
              {homepageData?.viewfinder_intro_screen_copy_2}
            </p>
            <div className="game-navigation">
              <button className="btn-primary" onClick={handleBackToHome}>
                Back
              </button>
              <button className="btn-primary inverted" onClick={startGame}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {gameState === 'playing' && (
        <div className="game-screen-play">
          <div className="game-header">
            <div className="level-indicator">Level {currentLevel}</div>
            <button
              className="exit-button"
              onClick={() => setShowExitPopup(true)}
            >
              Exit X
            </button>
          </div>

          <div className="game-content">
            <div className="game-instructions">
              <h3 className="piped inverted">{getInstructionText()}</h3>
              <p className={`feedback ${feedback.toLowerCase()}`}>{feedback}</p>
            </div>

            {renderGameGrid()}
          </div>
        </div>
      )}

      {gameState === 'levelComplete' && (
        <div className="completion-screen">
          <div className="game-header">
            <div className="level-indicator">Level {currentLevel}</div>
            <button
              className="exit-button"
              onClick={() => setShowExitPopup(true)}
            >
              Exit X
            </button>
          </div>
          <div className="completion-content">
            <h2>All Clear!</h2>
            <div className="revealed-image piped">
              <img
                src={BACKGROUND_IMAGES[currentLevel]}
                alt={`Level ${currentLevel} complete`}
              />
            </div>

            <p className="completion-time">
              Completed in{' '}
              <span className="completion-secs">
                {formatTime(levelTimes[levelTimes.length - 1]?.time || 0)}
              </span>
            </p>
          </div>
          <button className="btn-primary inverted" onClick={nextLevel}>
            Next
          </button>
        </div>
      )}

      {gameState === 'gameComplete' && (
        <div className="completion-screen final-completion">
          <div className="slides-topbar-main game-screen">
            <div className="logo-group logo-group--main">
              <div className="logo logo-main-container">
                <Logo
                  className="logo-main"
                  src={
                    homepageData?.level_62_logo_white?.url ||
                    homepageData?.level_62_logo_white
                  }
                  alt={
                    homepageData?.level_62_logo_white?.alt || 'Level 62 Logo'
                  }
                />
              </div>
            </div>
          </div>
          <div className="completion-content">
            <h2>You Did It!</h2>
            <p className="final-description">
              You successfully revealed the elevator's views. Well done.
            </p>

            <div className="level-summary">
              {levelTimes.map((levelTime, index) => (
                <div key={index} className="level-result">
                  <img
                    src={BACKGROUND_IMAGES[levelTime.level]}
                    alt={`Level ${levelTime.level}`}
                  />
                  <div className="level-info">
                    <span>Puzzle {levelTime.level}</span>
                    <span>{formatTime(levelTime.time)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="game-navigation">
              <button className="btn-primary" onClick={handleBackToHome}>
                Main Menu
              </button>
              <button className="btn-primary inverted" onClick={playAgain}>
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {showExitPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <button
              className="popup-close"
              onClick={() => setShowExitPopup(false)}
            >
              <span>×</span>
            </button>
            <div className="popup-body">
              <h3>
                Are you sure you <br />
                want to exit?
              </h3>
              <p>You will lose your progress.</p>
              <button
                className="popup-button btn-primary inverted"
                onClick={handleBackToHome}
              >
                Yes, I'm sure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClearView;
